import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useAuth } from '@/contexts/AuthContext';
import { useSystemStatus } from '@/api/system-configuration/use-system-configuration';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import { CountrySelect } from '@/components/ui/country-select';
import { InternationalPhoneInput } from '@/components/ui/international-phone-input';

const RegisterPage: React.FC = () => {
    const { t } = useTranslation('auth');
    const { t: tCommon } = useTranslation('common');
    const { enqueueSnackbar } = useSnackbar();
    const { register, isTrader } = useAuth();
    const {
        data: systemStatus,
        isLoading: isSystemStatusLoading,
        refreshSystemStatus,
    } = useSystemStatus();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        phone: '',
        country: '',
    });

    const [errors, setErrors] = useState<Record<string, string[]>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: [],
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            // Only allow customer registration
            await register(
                formData.name,
                formData.email,
                formData.password,
                formData.country,
                formData.phone
            );
        } catch (error: any) {
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            } else {
                enqueueSnackbar(t('registrationFailed'), { variant: 'error' });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const getPortalInfo = () => {
        if (isTrader()) {
            return {
                title: t('registerTitle'),
                subtitle: t('registerSubtitle'),
                loginText: t('alreadyHaveAccount'),
                loginLink: '/auth/login',
                allowRegistration: systemStatus?.registration_enabled === true,
            };
        } else {
            return {
                title: 'CRM Account Registration',
                loginText: t('alreadyHaveAccount'),
                loginLink: '/auth/login',
                switchLinkText: t('createTraderAccount'),
                allowRegistration: false,
            };
        }
    };

    const portalInfo = getPortalInfo();

    if (isSystemStatusLoading) {
        return (
            <>
                <h1 className="mb-2 text-center text-[length:var(--auth-title-size)] font-bold leading-tight tracking-tight">
                    {tCommon('loading')}
                </h1>
                <p className="text-muted-foreground text-center mb-8">{t('pleaseWaitStatus')}</p>
            </>
        );
    }

    if (!portalInfo.allowRegistration) {
        const message = isTrader() ? t('registrationDisabled') : t('registrationNotAvailableCRM');

        return (
            <>
                <h1 className="mb-2 text-center text-[length:var(--auth-title-size)] font-bold leading-tight tracking-tight">
                    {portalInfo.title}
                </h1>
                <p className="text-muted-foreground text-center mb-8">{message}</p>

                {isTrader() && (
                    <div className="text-center mb-4">
                        <Button
                            onClick={refreshSystemStatus}
                            variant="outline"
                            size="sm"
                            disabled={isSystemStatusLoading}
                        >
                            {isSystemStatusLoading ? tCommon('checking') : tCommon('checkAgain')}
                        </Button>
                    </div>
                )}

                <div className="text-center mt-8">
                    <p className="text-sm text-muted-foreground mb-2">
                        {portalInfo.loginText}{' '}
                        <Link to={portalInfo.loginLink} className="text-primary hover:underline">
                            {t('signIn')}
                        </Link>
                    </p>
                </div>
            </>
        );
    }

    return (
        <>
            <h1 className="mb-2 text-center text-[length:var(--auth-title-size)] font-bold leading-tight tracking-tight">
                {portalInfo.title}
            </h1>
            <p className="text-muted-foreground text-center mb-8">{portalInfo.subtitle}</p>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                    <Label htmlFor="name">{t('fullName')}</Label>
                    <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className={errors.name ? 'border-destructive' : ''}
                    />
                    {errors.name && <p className="text-sm text-destructive">{errors.name[0]}</p>}
                </div>

                <div className="space-y-2">
                    <Label htmlFor="email">{t('emailAddress')}</Label>
                    <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className={errors.email ? 'border-destructive' : ''}
                    />
                    {errors.email && <p className="text-sm text-destructive">{errors.email[0]}</p>}
                </div>

                <div className="space-y-2">
                    <Label htmlFor="phone">{t('phoneNumber')}</Label>
                    <InternationalPhoneInput
                        value={formData.phone}
                        onChange={value => {
                            setFormData(prev => ({ ...prev, phone: value }));
                            if (errors.phone) {
                                setErrors(prev => ({ ...prev, phone: [] }));
                            }
                        }}
                        placeholder={t('phoneNumber')}
                        className={errors.phone ? 'border-destructive' : ''}
                    />
                    {errors.phone && <p className="text-sm text-destructive">{errors.phone[0]}</p>}
                </div>

                <div className="space-y-2">
                    <Label htmlFor="country">{t('country')}</Label>
                    <CountrySelect
                        value={formData.country}
                        onChange={value => {
                            setFormData(prev => ({ ...prev, country: value }));
                            if (errors.country) {
                                setErrors(prev => ({ ...prev, country: [] }));
                            }
                        }}
                        placeholder={t('selectCountry')}
                    />
                    {errors.country && (
                        <p className="text-sm text-destructive">{errors.country[0]}</p>
                    )}
                </div>

                <div className="space-y-2">
                    <Label htmlFor="password">{t('password')}</Label>
                    <div className="relative">
                        <Input
                            id="password"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            value={formData.password}
                            onChange={handleChange}
                            required
                            className={errors.password ? 'border-destructive pe-10' : 'pe-10'}
                        />
                        <button
                            type="button"
                            className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? (
                                <EyeOff className="h-4 w-4" />
                            ) : (
                                <Eye className="h-4 w-4" />
                            )}
                        </button>
                    </div>
                    {errors.password && (
                        <p className="text-sm text-destructive">{errors.password[0]}</p>
                    )}
                </div>

                <div className="space-y-2">
                    <Label htmlFor="password_confirmation">{t('confirmPassword')}</Label>
                    <div className="relative">
                        <Input
                            id="password_confirmation"
                            name="password_confirmation"
                            type={showPasswordConfirmation ? 'text' : 'password'}
                            value={formData.password_confirmation}
                            onChange={handleChange}
                            required
                            className={
                                errors.password_confirmation ? 'border-destructive pe-10' : 'pe-10'
                            }
                        />
                        <button
                            type="button"
                            className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                        >
                            {showPasswordConfirmation ? (
                                <EyeOff className="h-4 w-4" />
                            ) : (
                                <Eye className="h-4 w-4" />
                            )}
                        </button>
                    </div>
                    {errors.password_confirmation && (
                        <p className="text-sm text-destructive">
                            {errors.password_confirmation[0]}
                        </p>
                    )}
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? t('creatingAccount') : t('createTraderAccount')}
                </Button>

                <div className="text-center">
                    <p className="text-sm text-muted-foreground">
                        {portalInfo.loginText}{' '}
                        <Link to={portalInfo.loginLink} className="text-primary hover:underline">
                            {t('signIn')}
                        </Link>
                    </p>
                </div>
            </form>
        </>
    );
};

export default RegisterPage;
